<template>
  <div class="row">
    <div class="col-12 col-lg-6">
      <div class="form-group">
        <label>
          <strong>Published <span id="published_tooltip" class="ml-2 la la-question-circle" /></strong>
        </label>
        <b-tooltip target="published_tooltip" triggers="hover">
          <div v-sanitise="tooltips.published_tooltip" />
        </b-tooltip>
        <div>
          <span class="kt-switch kt-switch--icon">
            <label>
              <input v-model="episode.published"
                     name="Published"
                     type="checkbox"
                     :checked="episode.published"
                     @change="publishValueToggled">
              <span />
            </label>
          </span>
        </div>
      </div>
      <div v-if="!episode.published" class="row">
        <div class="col-12 col-sm-6">
          <div class="form-group">
            <label>
              <strong>Publish Date * <span id="episode_publish_date_tooltip" class="ml-2 la la-question-circle" /></strong>
            </label>
            <b-tooltip target="episode_publish_date_tooltip" triggers="hover">
              <div v-sanitise="tooltips.episode_publish_date_tooltip" />
            </b-tooltip>
            <datetime id="js-publish_start"
                      v-model="episode.publish_start"
                      input-class="form-control kt-input"
                      type="datetime"
                      :max-datetime="max_publish_start_iso8601"
                      placeholder="yyyy-MM-dd HH:mm:ss"
                      format="yyyy-MM-dd HH:mm:ss" />
            <!-- Spoof field for validation, see `publish_start_rules` -->
            <input v-model="selected_publish_date"
                   v-validate="publish_start_rules"
                   data-vv-scope="episode-publishing-form"
                   type="text"
                   name="Episode Publish Date"
                   class="d-none">
            <p v-show="errors.has('Episode Publish Date', 'episode-publishing-form')" class="help text-ss-danger">
              {{ errors.first('Episode Publish Date', 'episode-publishing-form') }}
            </p>
          </div>
        </div>
        <div class="col-12 col-sm-6">
          <div class="form-group">
            <label>
              <strong>Unpublish Date <span id="episode_unpublish_date_tooltip" class="ml-2 la la-question-circle" /></strong>
            </label>
            <b-tooltip target="episode_unpublish_date_tooltip" triggers="hover">
              <div v-sanitise="tooltips.episode_unpublish_date_tooltip" />
            </b-tooltip>
            <datetime id="js-publish_stop"
                      v-model="episode.publish_stop"
                      input-class="form-control kt-input"
                      type="datetime"
                      :min-datetime="min_publish_stop_iso8601"
                      placeholder="yyyy-MM-dd HH:mm:ss"
                      format="yyyy-MM-dd HH:mm:ss" />
            <!-- Spoof field for validation, see `publish_stop_rules` -->
            <input v-model="selected_unpublish_date"
                   v-validate="publish_stop_rules"
                   data-vv-scope="episode-publishing-form"
                   type="text"
                   name="Episode Unpublish Date"
                   class="d-none">
            <p v-show="errors.has('Episode Unpublish Date', 'episode-publishing-form')" class="help text-ss-danger">
              {{ errors.first('Episode Unpublish Date', 'episode-publishing-form') }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapWritableState } from 'pinia';
import { useEpisodeManageStore } from "VueStore/app/episodeManage";
import PodcastValidateMixin from "VueMixins/PodcastValidateMixin";
import TooltipsMixin from "VueMixins/TooltipsMixin";
import VeeValidate from 'vee-validate';
VeeValidate.config.useConstraintAttrs = false;

export default {
  mixins: [PodcastValidateMixin, TooltipsMixin],
  computed: {
    ...mapWritableState(useEpisodeManageStore, ['episode']),

    /**
     * The datetime picker's value is inserted into a hidden text input, and validation is performed on that instead.
     * - required field
     * - must be in the format `yyyy-MM-ss HH:mm:ss`
     * - must be less than the publish stop date, if it exists
     *
     * The custom rule for requiring this date be less than the publish stop date is in `vee-validate-custom-rules.js`.
     *
     * There is some redundancy; the date picker prevents malformed inputs and also keeps the dates in-range already.
     */
    publish_start_rules() {
      if (!this.episode.published) {
        return `required|date_format:yyyy-MM-dd HH:mm:ss|episode_publish_start_before_publish_stop:${this.max_publish_start}`;
      }
      return '';
    },

    /**
     * The datetime picker's value is inserted into a hidden text input, and validation is performed on that instead.
     * - must be in the format `yyyy-MM-ss HH:mm:ss`
     * - must be greater than the publish start date, if it exists
     *
     * The custom rule for requiring this date be greater than the publish start date is in `vee-validate-custom-rules.js`.
     *
     * There is some redundancy; the date picker prevents malformed inputs and also keeps the dates in-range already.
     */
    publish_stop_rules() {
      if (this.episode.publish_start) {
        return `date_format:yyyy-MM-dd HH:mm:ss|episode_publish_stop_after_publish_start:${this.min_publish_stop}`;
      }
      return '';
    },

    max_publish_start() {
      return this.episode.publish_stop ?? null;
    },

    min_publish_stop() {
      return this.episode.publish_start ?? null;
    },

    max_publish_start_iso8601() {
      if (this.max_publish_start) {
        return moment(this.max_publish_start).format();
      }

      return null;
    },

    min_publish_stop_iso8601() {
      if (this.min_publish_stop) {
        return moment(this.min_publish_stop).format();
      }

      return null;
    },

    selected_unpublish_date() {
      return this.episode.publish_stop ? moment(this.episode.publish_stop).format('YYYY-MM-DD HH:mm:ss') : null;
    },
    selected_publish_date() {
      return this.episode.publish_start ? moment(this.episode.publish_start).format('YYYY-MM-DD HH:mm:ss') : null;
    },
  },
  methods: {
    publishValueToggled() {
      // If toggled to published state, we want to unset the publish_start and stop values
      if (this.episode.published) {
        this.episode.publish_start = null;
        this.episode.publish_stop = null;
      }
    },
  },
};
</script>
